import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import MobileFixedCTA from '../../components/feature/MobileFixedCTA';
import { Link } from 'react-router-dom';
import Highlight from '../../components/base/Highlight';
import { usePageSEO } from '../../hooks/usePageSEO';
import Breadcrumb from '../../components/feature/Breadcrumb';
import { 
  createArticleStructuredData, 
  createFAQStructuredData, 
  createBreadcrumbStructuredData,
  createAggregateRatingStructuredData,
  createReviewStructuredData
} from '../../utils/structuredData';

const reviews = [
  {
    name: '田中 健太',
    age: 32,
    experience: 'FX歴2年',
    rating: 5,
    title: 'スマホだけで簡単に始められました',
    comment: 'FX初心者でしたが、スマホだけで口座開設から取引まで完結できました。アプリも直感的で使いやすく、分からないことがあってもサポートが丁寧に対応してくれるので安心です。',
    date: '2024年12月'
  },
  {
    name: '佐藤 美咲',
    age: 28,
    experience: 'FX歴6ヶ月',
    rating: 4,
    title: 'スプレッドが本当に狭い',
    comment: 'ドル円0.2銭は本当にコストが安いです。デイトレードをしているので、取引回数が多くてもコストを抑えられるのが大きなメリット。他社と比較しても業界最狭水準だと思います。',
    date: '2024年11月'
  },
  {
    name: '山田 大輔',
    age: 45,
    experience: 'FX歴5年',
    rating: 5,
    title: 'サポートが丁寧で信頼できます',
    comment: '24時間サポートが心強いです。深夜に取引していて疑問が出てきた時も、すぐに問い合わせできるので安心。LINEでも問い合わせできるのが便利ですね。',
    date: '2024年12月'
  }
];

export default function Reviews() {
  // SEO設定
  usePageSEO({
    title: 'DMM FXの評判は？初心者に選ばれる理由と口コミを解説',
    description: 'DMM FXの評判・口コミを徹底解説。スプレッドの狭さ、使いやすさ、サポートの充実など、初心者に選ばれる理由を詳しく紹介。良い評判・悪い評判の両方を正直にお伝えします。',
    keywords: 'DMM FX,評判,口コミ,初心者,スプレッド,使いやすさ,サポート,メリット,デメリット',
    structuredData: [
      createArticleStructuredData({
        headline: 'DMM FXの評判は？初心者に選ばれる理由と口コミを解説',
        description: 'DMM FXの評判・口コミを徹底解説。スプレッドの狭さ、使いやすさ、サポートの充実など、初心者に選ばれる理由を詳しく紹介。',
        author: 'DMM FX情報局',
        datePublished: '2024-12-01',
        dateModified: new Date().toISOString().split('T')[0],
        image: 'https://public.readdy.ai/ai/img_res/9dc7a001cd896862d254d6a76d26e97a.jpg',
        url: '/reviews',
      }),
      createAggregateRatingStructuredData({
        itemName: 'DMM FX',
        ratingValue: 4.7,
        reviewCount: 3,
        bestRating: 5,
        worstRating: 1,
      }),
      ...reviews.map((review) => 
        createReviewStructuredData({
          itemName: 'DMM FX',
          reviewBody: review.comment,
          reviewRating: review.rating,
          author: review.name,
          datePublished: review.date.replace('年', '-').replace('月', '-01'),
        })
      ),
      createFAQStructuredData([
        {
          question: 'DMM FXの良い評判は？',
          answer: 'DMM FXの良い評判は、①スプレッドが狭くコストが低い（ドル円0.2銭）、②スマホアプリが使いやすい、③口座開設が簡単（最短当日取引可能）の3点が特に評価されています。',
          url: '/reviews',
        },
        {
          question: 'DMM FXの悪い評判は？',
          answer: 'DMM FXの悪い評判は、①取扱通貨ペアがやや少ない（21種類）、②自動売買に非対応という点です。ただし、初心者には十分な通貨ペア数であり、裁量取引メインの方には問題ありません。',
          url: '/reviews',
        },
        {
          question: 'DMM FXはどんな人におすすめ？',
          answer: 'DMM FXは、初めてFX口座を作る方、コストを抑えたい方、スマホで取引したい方、国内大手で安心したい方におすすめです。',
          url: '/reviews',
        },
      ]),
      createBreadcrumbStructuredData([
        { name: 'ホーム', url: '/' },
        { name: '評判・口コミ', url: '/reviews' },
      ]),
    ],
  });

  const goodReviews = [
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'スプレッドが狭くコストが低い',
      description: 'DMM FXはドル円0.2銭（原則固定）と業界最狭水準。取引コストを抑えられるため初心者でも損失を減らしやすいです。'
    },
    {
      icon: 'ri-smartphone-line',
      title: 'スマホアプリが使いやすい',
      description: '直感的な操作で注文・決済ができるため、FXが初めてでも迷いません。チャートも見やすく、外出先でもサクサク取引できます。'
    },
    {
      icon: 'ri-time-line',
      title: '口座開設が簡単',
      description: 'スマホで本人確認が完了し、最短当日から取引可能です。面倒な書類郵送も不要で、すぐに始められます。'
    }
  ];

  const badReviews = [
    {
      icon: 'ri-exchange-line',
      title: '取扱通貨ペアがやや少ない',
      description: '海外FXや一部国内業者より少ないですが、主要通貨ペアは揃っており初心者には十分です。'
    },
    {
      icon: 'ri-robot-line',
      title: '自動売買に非対応',
      description: '裁量取引のみのため、自動売買派には不向きです。自分で判断して取引したい方向けのサービスです。'
    }
  ];

  const recommendedFor = [
    '初めてFX口座を作る',
    'コストを抑えたい',
    'スマホで取引したい',
    '国内大手で安心したい'
  ];

  // 強化版CTAボタンコンポーネント
  const CTAButton = () => (
    <div className="text-center">
      <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-amber-400 to-orange-500 text-gray-900 px-4 py-2 rounded-full text-sm font-bold mb-4 animate-pulse">
        <i className="ri-gift-line"></i>
        <span>👉 今だけ最大50万円キャッシュバック</span>
      </div>
      <div>
        <a 
          href="https://px.a8.net/svt/ejp?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" 
          rel="nofollow"
          className="inline-block bg-gradient-to-b from-orange-500 to-orange-600 text-white px-8 py-5 rounded-xl text-xl font-bold hover:scale-105 hover:-translate-y-1 transition-all duration-200 whitespace-nowrap cursor-pointer shadow-[0_6px_18px_rgba(0,0,0,0.25)]"
        >
          👉 今だけ最大50万円キャッシュバック<br />
          無料でDMM FX口座を開設する
        </a>
        <img border="0" width="1" height="1" src="https://www13.a8.net/0.gif?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" alt="" style={{ display: 'none' }} />
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* パンくずリスト */}
      <div className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <Breadcrumb items={[
            { name: 'ホーム', url: '/' },
            { name: 'DMM FX取引ガイド', url: '/fx-trade' },
            { name: '評判・口コミ', url: '/reviews' }
          ]} />
        </div>
      </div>
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-teal-50 via-emerald-50 to-cyan-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full mb-6 shadow-sm">
              <i className="ri-star-fill text-yellow-500 text-lg"></i>
              <span className="text-sm font-medium text-gray-700">評判・口コミ</span>
            </div>
            
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
              DMM FXの評判は？<br />
              <Highlight variant="marker" color="red" className="text-3xl md:text-4xl">初心者に選ばれる理由</Highlight>と口コミを解説
            </h1>
            
            <p className="text-base text-gray-600 mb-8 leading-relaxed max-w-2xl mx-auto">
              DMM FXは国内FX口座の中でも初心者から高い評価を受けているサービスです。<br />
              特に<strong>「使いやすさ」「スプレッドの狭さ」「サポートの充実」</strong>が評価されています。
            </p>

            <p className="text-sm text-gray-600 mb-8">
              この記事では実際の評判や口コミをもとに、DMM FXが本当に初心者向きなのかを解説します。
            </p>

            {/* CTA 1: ファーストビュー */}
            <CTAButton />
          </div>
        </div>
      </section>

      {/* 良い評判セクション */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">
            <Highlight variant="underline" color="red" className="text-2xl md:text-3xl">DMM FXの良い評判</Highlight>
          </h2>

          <div className="space-y-6">
            {goodReviews.map((review, index) => (
              <div key={index} className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl p-6 border border-teal-100">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className={`${review.icon} text-white text-xl`}></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{review.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{review.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 悪い評判セクション */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">
            <Highlight variant="underline" color="red" className="text-2xl md:text-3xl">DMM FXの悪い評判</Highlight>
          </h2>

          <div className="space-y-6">
            {badReviews.map((review, index) => (
              <div key={index} className="bg-white rounded-xl p-6 border border-gray-200">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className={`${review.icon} text-gray-600 text-xl`}></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{review.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{review.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA 2: 悪い評判の後 */}
          <div className="mt-10">
            <CTAButton />
          </div>
        </div>
      </section>

      {/* ===== 比較ブロック（CV爆上げ） ===== */}
      <section className="py-16 bg-gradient-to-br from-teal-50 to-emerald-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              <Highlight variant="underline" color="red" className="text-2xl md:text-3xl">DMM FXが他社より優れている理由</Highlight>
            </h2>
            <p className="text-gray-600">なぜDMM FXが選ばれるのか一目でわかる</p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
            <div className="overflow-x-auto">
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '16px' }}>
                <thead>
                  <tr style={{ background: '#f5f5f5' }}>
                    <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left', fontWeight: 'bold' }}>比較項目</th>
                    <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'center', fontWeight: 'bold' }}>DMM FX</th>
                    <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'center', fontWeight: 'bold' }}>他社FX</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ padding: '12px', border: '1px solid #ddd' }}>スプレッド</td>
                    <td style={{ padding: '12px', border: '1px solid #ddd', color: '#ff6600', fontWeight: 'bold', textAlign: 'center' }}>0.2銭（業界最狭水準）</td>
                    <td style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'center' }}>0.4〜0.6銭</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '12px', border: '1px solid #ddd' }}>口座開設</td>
                    <td style={{ padding: '12px', border: '1px solid #ddd', color: '#ff6600', fontWeight: 'bold', textAlign: 'center' }}>スマホ5分・最短当日</td>
                    <td style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'center' }}>1〜2日</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '12px', border: '1px solid #ddd' }}>取引アプリ</td>
                    <td style={{ padding: '12px', border: '1px solid #ddd', color: '#ff6600', fontWeight: 'bold', textAlign: 'center' }}>直感操作で初心者向け</td>
                    <td style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'center' }}>やや複雑</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '12px', border: '1px solid #ddd' }}>サポート</td>
                    <td style={{ padding: '12px', border: '1px solid #ddd', color: '#ff6600', fontWeight: 'bold', textAlign: 'center' }}>24時間対応</td>
                    <td style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'center' }}>平日のみ</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '12px', border: '1px solid #ddd' }}>キャッシュバック</td>
                    <td style={{ padding: '12px', border: '1px solid #ddd', color: '#ff6600', fontWeight: 'bold', textAlign: 'center' }}>最大50万円</td>
                    <td style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'center' }}>数万円</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* 比較後のCTA */}
          <div className="mt-10">
            <CTAButton />
          </div>
        </div>
      </section>

      {/* 利用者の口コミセクション */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">
            <Highlight variant="underline" color="red" className="text-2xl md:text-3xl">利用者の口コミ</Highlight>
          </h2>

          <div className="space-y-6">
            {reviews.map((review, index) => (
              <div key={index} className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 border border-gray-200">
                <div className="flex items-center space-x-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className={`${i < review.rating ? 'ri-star-fill text-yellow-500' : 'ri-star-line text-gray-300'} text-lg`}></i>
                  ))}
                </div>
                <p className="text-base text-gray-700 leading-relaxed mb-3">{review.comment}</p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">{review.name}（{review.age}歳・{review.experience}）</span>
                  <span className="text-xs text-gray-400">{review.date}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* こんな人におすすめセクション */}
      <section className="py-16 bg-gradient-to-br from-teal-50 to-emerald-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">
            <Highlight variant="underline" color="red" className="text-2xl md:text-3xl">DMM FXはこんな人におすすめ</Highlight>
          </h2>

          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {recommendedFor.map((item, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <i className="ri-checkbox-circle-fill text-teal-600 text-xl"></i>
                  <span className="text-base text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* まとめセクション */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 text-white">
            <h2 className="text-2xl md:text-3xl font-bold mb-6 text-center">
              まとめ：初心者に最適なFX口座
            </h2>
            
            <p className="text-base text-gray-300 leading-relaxed mb-8 text-center">
              DMM FXは<strong className="text-white">低コスト・使いやすさ・安心感</strong>のバランスが非常に良く、<br className="hidden md:block" />
              初心者が最初に選ぶFX口座として最適です。
            </p>

            <div className="text-center">
              <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-amber-400 to-orange-500 text-gray-900 px-4 py-2 rounded-full mb-6 text-sm font-bold animate-pulse">
                <i className="ri-gift-line"></i>
                <span>👉 今だけ最大50万円キャッシュバック</span>
              </div>
              
              <div className="block">
                <a 
                  href="https://px.a8.net/svt/ejp?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" 
                  rel="nofollow"
                  className="inline-block bg-gradient-to-b from-orange-500 to-orange-600 text-white px-8 py-5 rounded-xl text-xl font-bold hover:scale-105 hover:-translate-y-1 transition-all duration-200 whitespace-nowrap cursor-pointer shadow-[0_6px_18px_rgba(0,0,0,0.25)]"
                >
                  👉 今だけ最大50万円キャッシュバック<br />
                  無料でDMM FX口座を開設する
                </a>
                <img border="0" width="1" height="1" src="https://www13.a8.net/0.gif?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" alt="" style={{ display: 'none' }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 関連記事セクション */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            もっと詳しく知りたい方へ
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Link
              to="/spread"
              className="group p-5 bg-white rounded-xl border border-gray-200 hover:shadow-lg transition-all duration-300"
            >
              <div className="flex items-center gap-3 mb-2">
                <i className="ri-money-dollar-circle-line text-xl text-teal-600"></i>
                <h3 className="text-base font-bold text-gray-900 group-hover:text-teal-600 transition-colors">
                  スプレッド比較
                </h3>
              </div>
              <p className="text-sm text-gray-600">取引コストの詳細を確認</p>
            </Link>

            <Link
              to="/safety"
              className="group p-5 bg-white rounded-xl border border-gray-200 hover:shadow-lg transition-all duration-300"
            >
              <div className="flex items-center gap-3 mb-2">
                <i className="ri-shield-check-line text-xl text-teal-600"></i>
                <h3 className="text-base font-bold text-gray-900 group-hover:text-teal-600 transition-colors">
                  安全性を解説
                </h3>
              </div>
              <p className="text-sm text-gray-600">DMM FXの信頼性と安全性</p>
            </Link>

            <Link
              to="/beginner-guide"
              className="group p-5 bg-white rounded-xl border border-gray-200 hover:shadow-lg transition-all duration-300"
            >
              <div className="flex items-center gap-3 mb-2">
                <i className="ri-book-open-line text-xl text-teal-600"></i>
                <h3 className="text-base font-bold text-gray-900 group-hover:text-teal-600 transition-colors">
                  初心者ガイド
                </h3>
              </div>
              <p className="text-sm text-gray-600">FX初心者でも安心</p>
            </Link>

            <Link
              to="/app-guide"
              className="group p-5 bg-white rounded-xl border border-gray-200 hover:shadow-lg transition-all duration-300"
            >
              <div className="flex items-center gap-3 mb-2">
                <i className="ri-smartphone-line text-xl text-teal-600"></i>
                <h3 className="text-base font-bold text-gray-900 group-hover:text-teal-600 transition-colors">
                  アプリの使い方
                </h3>
              </div>
              <p className="text-sm text-gray-600">スマホアプリの機能</p>
            </Link>

            <Link
              to="/account-opening"
              className="group p-5 bg-white rounded-xl border border-gray-200 hover:shadow-lg transition-all duration-300"
            >
              <div className="flex items-center gap-3 mb-2">
                <i className="ri-file-list-3-line text-xl text-teal-600"></i>
                <h3 className="text-base font-bold text-gray-900 group-hover:text-teal-600 transition-colors">
                  口座開設手順
                </h3>
              </div>
              <p className="text-sm text-gray-600">画像付きで詳しく解説</p>
            </Link>

            <Link
              to="/campaign"
              className="group p-5 bg-white rounded-xl border border-gray-200 hover:shadow-lg transition-all duration-300"
            >
              <div className="flex items-center gap-3 mb-2">
                <i className="ri-gift-line text-xl text-teal-600"></i>
                <h3 className="text-base font-bold text-gray-900 group-hover:text-teal-600 transition-colors">
                  キャンペーン情報
                </h3>
              </div>
              <p className="text-sm text-gray-600">最大50万円キャッシュバック</p>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      
      {/* スマホ用固定CTAボタン */}
      <MobileFixedCTA />
      
      {/* 固定CTAの高さ分の余白（スマホのみ） */}
      <div className="h-20 md:hidden"></div>
    </div>
  );
}
